import type * as React from 'react'
import { useLayoutEffect, useRef } from 'react'

import { readsRtl, sigEase, signatureMoves } from '../../lib/signature/motion'
import { cn } from '../../lib/utils'

/**
 * Progress as a pen stroke (DESIGN.md "The pen in controls"): a 16px stroke
 * that draws from the reading start, lifts from the start and draws again,
 * 1.1s per stroke, for as long as the work runs (one of the two bounded
 * exceptions to The Once-per-Change Rule). It replaces any spinner.
 *
 * Decorative: the busy state is carried by `aria-busy` on the control (or a
 * live region) beside it. Under reduced motion the stroke rests, fully drawn.
 */
function PenProgress({ className, ...props }: React.ComponentProps<'span'>) {
  const ref = useRef<HTMLSpanElement>(null)

  useLayoutEffect(() => {
    const stroke = ref.current
    if (!stroke || !signatureMoves()) return
    // clip-path has no logical keyword, so the reading start is resolved here.
    const [start, end] = readsRtl(stroke)
      ? ['inset(0 0 0 100%)', 'inset(0 100% 0 0)']
      : ['inset(0 100% 0 0)', 'inset(0 0 0 100%)']
    const animation = stroke.animate(
      [
        { clipPath: start, easing: sigEase('--sig-ease-write') },
        { clipPath: 'inset(0 0 0 0)', offset: 0.55, easing: sigEase('--sig-ease-write') },
        { clipPath: end },
      ],
      { duration: 1100, iterations: Number.POSITIVE_INFINITY },
    )
    return () => animation.cancel()
  }, [])

  return (
    <span
      ref={ref}
      data-slot="pen-progress"
      aria-hidden="true"
      className={cn('inline-block h-0.5 w-4 shrink-0 bg-current', className)}
      {...props}
    />
  )
}

export { PenProgress }
